import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X, MessageSquare, Users2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePresence, type PresenceState } from '@/contexts/PresenceContext'
import { PresenceDot, presenceLabelKey } from '@/components/presence/PresenceDot'
import { useUser } from '@/app/store/auth'
import api from '@/lib/api/axios'

interface UserInfo {
    id: number
    name: string
    email: string
}

interface OnlineUsersPanelProps {
    open: boolean
    onClose: () => void
}

export function OnlineUsersPanel({ open, onClose }: OnlineUsersPanelProps) {
    const { t } = useTranslation()
    const { onlineUsers, getUserStatus } = usePresence()
    const currentUser = useUser()
    const [allUsers, setAllUsers] = useState<UserInfo[]>([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open) return
        setLoading(true)
        api.get('/api/users', { params: { page: 1, limit: 200 } })
            .then(res => {
                const data = (res.data as any)?.data || res.data
                if (Array.isArray(data)) {
                    setAllUsers(data.map((u: any) => ({
                        id: u.id,
                        name: u.name || u.email || `User ${u.id}`,
                        email: u.email || '',
                    })))
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [open])

    const handleStartChat = useCallback(async (userId: number) => {
        try {
            await api.post('/api/ai/user-chat/invite', { inviteeUserId: userId })
            onClose()
        } catch (err) {
            console.error('Failed to start chat:', err)
        }
    }, [onClose])

    if (!open) return null

    const onlineUserList = allUsers.filter(u => onlineUsers.has(u.id) && u.id !== currentUser?.id)

    return (
        <div className="fixed inset-y-0 right-0 w-80 bg-background border-l shadow-xl z-[200] flex flex-col animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="flex items-center gap-2">
                    <Users2 className="h-5 w-5 text-primary" />
                    <h2 className="text-base font-semibold">{t('onlineUsers.title', 'Online Users')}</h2>
                    <span className="text-xs text-muted-foreground">({onlineUserList.length})</span>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* User list */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                        {t('common.loading', 'Loading...')}
                    </div>
                ) : onlineUserList.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                        {t('onlineUsers.noUsers', 'No other users online')}
                    </div>
                ) : (
                    <div className="divide-y">
                        {onlineUserList.map(user => (
                            <UserRow
                                key={user.id}
                                user={user}
                                status={getUserStatus(user.id)}
                                onStartChat={handleStartChat}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

function UserRow({ user, status, onStartChat }: {
    user: UserInfo
    status: PresenceState
    onStartChat: (userId: number) => void
}) {
    const { t } = useTranslation()
    const initials = user.name
        .split(' ')
        .map(w => w[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase() || '?'

    return (
        <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors group">
            <div className="relative flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                    {initials}
                </div>
                {/* The shared dot: a locked screen shows amber here rather than the green
                    that would have someone ring a desk nobody is at. */}
                <PresenceDot status={status} className="absolute -top-0.5 -right-0.5 h-3 w-3" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                    {t(presenceLabelKey(status).key, presenceLabelKey(status).fallback)} · {user.email}
                </p>
            </div>
            <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 hover:text-blue-700"
                onClick={() => onStartChat(user.id)}
                title={t('common.startChat', 'Start chat')}
            >
                <MessageSquare className="h-4 w-4" />
            </Button>
        </div>
    )
}
